// #region Imports
import { defineComponent } from 'vue';
import { RestApiAdapter } from './adapters/RestApiAdapter';
import ApiSelectTableDropdown from './g-search-box__table.vue';
import ApiSelectTreeDropdown from './g-search-box__tree.vue';
import ApiSelectCustomDropdown from './g-search-box__custom.vue';
import { EntityAdapter } from './adapters/EntityAdapter';
// #endregion
// #region Core
export default defineComponent({
    // #region Class
    name: 'g-search-box',
    // #endregion
    // #region Components
    components: {
        'g-search-box__table': ApiSelectTableDropdown,
        'g-search-box__tree': ApiSelectTreeDropdown,
        'g-search-box__custom': ApiSelectCustomDropdown,
    },
    // #endregion
    // #region Props
    props: {
        modelValue: {
            type: String,
            default: null,
        },
        dataStore: {
            type: Object,
            default: null,
        },
        dataAdapter: {
            type: Object,
            default: null,
        },
        dataArgs: {
            type: Object,
            default: null,
        },
        mode: {
            type: String,
            default: 'client',
        },
        idField: {
            type: String,
            default: 'id',
        },
        keyField: {
            type: String,
            default: 'key',
        },
        labelField: {
            type: String,
            default: 'label',
        },
        iconField: {
            type: String,
            default: null,
        },
        searchParam: {
            type: String,
            default: 'q',
        },
        parentField: {
            type: String,
            default: 'parentId',
        },
        pageParam: {
            type: String,
            default: 'page',
        },
        columns: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            required: false
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        pageSize: {
            type: Number,
            default: 20,
        },
        dropdownMode: {
            type: String,
            default: 'table',
        },
        dropDownHeight: {
            type: Number,
            default: 280,
        },
        selectLeafOnly: {
            type: Boolean,
            default: false,
        },
        showClearButton: {
            type: Boolean,
            default: true,
        }
    },
    // #endregion
    // #region Emits
    emits: {
        'update:modelValue': (_id) => true,
        select: (_item) => true,
    },
    // #endregion
    // #region Data
    data() {
        return {
            adapter: null,
            items: [],
            selectedItem: null,
            searchQuery: '',
            isOpen: false,
            isLoading: false,
            isLoadingById: false,
            currentPage: 1,
            hasMore: true,
            isLoadingMore: false,
            searchDebounceTimer: null,
            searchAbortController: null,
            expandedIds: [],
            highlightedIndex: -1,
            _inputPosition: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: 0,
                height: 0,
                windowHeight: window.innerHeight,
                windowWidth: window.innerWidth,
                differenceToBottom: 0,
                dropDownHeight: 0,
                dropDownOnTop: false
            },
        };
    },
    // #endregion
    // #region Computed
    computed: {
        dropDownStyle() {
            //#region Vars
            let tHeight = this.dropDownHeight == 0 ? 280 : this.dropDownHeight;
            let tWidth = this._inputPosition.width < 400 ? 400 : this._inputPosition.width;
            // #endregion
            // #region Top
            if (this._inputPosition.dropDownOnTop) {
                return {
                    "width": tWidth + 'px',
                    "max-width": tWidth + 'px',
                    "min-height": tHeight + 'px',
                    "max-height": tHeight + 'px',
                    "top": (this._inputPosition.top - tHeight - 5) + 'px',
                };
            }
            // #endregion
            // #region Bottom
            else {
                return {
                    "width": tWidth + 'px',
                    "max-width": tWidth + 'px',
                    "max-height": tHeight + 'px',
                    "top": (this._inputPosition.bottom + 5) + 'px',
                };
            }
            // #endregion
        },
        displayKey() {
            if (this.isOpen) {
                return this.searchQuery;
            }
            if (!this.selectedItem) {
                return '';
            }
            return String(this.selectedItem[this.keyField] ?? '');
        },
        displayLabel() {
            if (!this.selectedItem) {
                return '';
            }
            return String(this.selectedItem[this.labelField] ?? '');
        },
        displayItems() {
            if (this.mode === 'server')
                return this.items;
            const q = this.searchQuery.trim().toLowerCase();
            if (!q)
                return this.items;
            const fields = this.columns.length > 0
                ? this.columns.map(col => col.field)
                : Object.keys(this.items[0] ?? {});
            return this.items.filter(item => fields.some(f => {
                const v = item[f];
                return v != null && String(v).toLowerCase().includes(q);
            }));
        },
        rowCount() {
            return this.dropdownMode === 'tree'
                ? this.visibleTreeNodes.length
                : this.displayItems.length;
        },
        resolvedAdapter() {
            if (!this.adapter) {
                if (this.dataAdapter) {
                    this.adapter = this.dataAdapter;
                }
                else if (this.dataStore) {
                    if (this.dataStore.type === 'entity_list') {
                        this.adapter = new EntityAdapter({
                            ...this.dataStore,
                            getArgs: () => this.dataArgs,
                            appHost: this.$appHost,
                            keyFields: this.idField,
                        });
                    }
                    else if (this.dataStore.type === 'api') {
                        this.adapter = new RestApiAdapter({
                            ...this.dataStore,
                            appHost: this.$appHost,
                            searchParam: this.searchParam,
                            pageParam: this.pageParam,
                            idField: this.idField,
                        });
                    }
                    else if (this.dataStore.type === 'array') {
                    }
                    else {
                        //Do nothing
                    }
                }
                else {
                    //Do nothing
                }
            }
            return this.adapter;
        },
        visibleTreeNodes() {
            const childrenMap = this.buildChildrenMap();
            const roots = this.items.filter(item => !item[this.parentField]);
            const expandedSet = new Set(this.expandedIds);
            const q = this.searchQuery.trim().toLowerCase();
            let matchingIds = null;
            let ancestorIds = null;
            if (q) {
                matchingIds = new Set();
                ancestorIds = new Set();
                const itemById = new Map(this.items.map(i => [String(i[this.idField]), i]));
                for (const item of this.items) {
                    const matches = this.columns.some(col => {
                        const v = item[col.field];
                        return v != null && String(v).toLowerCase().includes(q);
                    });
                    if (!matches)
                        continue;
                    matchingIds.add(String(item[this.idField]));
                    let parentId = item[this.parentField] ? String(item[this.parentField]) : null;
                    while (parentId) {
                        ancestorIds.add(parentId);
                        const parent = itemById.get(parentId);
                        parentId = parent?.[this.parentField] ? String(parent[this.parentField]) : null;
                    }
                }
            }
            const result = [];
            const traverse = (items, depth) => {
                for (const item of items) {
                    const id = String(item[this.idField]);
                    const children = childrenMap.get(id) ?? [];
                    const hasChildren = children.length > 0;
                    if (matchingIds && ancestorIds) {
                        if (!matchingIds.has(id) && !ancestorIds.has(id))
                            continue;
                    }
                    result.push({ item, depth, hasChildren, isLeaf: !hasChildren });
                    if (hasChildren && (expandedSet.has(id) || ancestorIds?.has(id))) {
                        traverse(children, depth + 1);
                    }
                }
            };
            traverse(roots, 0);
            return result;
        },
    },
    // #endregion
    // #region Constructor
    async mounted() {
        // #region Events
        document.addEventListener('mousedown', this.onClickOutside);
        // #endregion
        // #region Data
        if (this.mode === 'client') {
            await this.loadAllData();
        }
        if (this.modelValue) {
            await this.loadById(this.modelValue);
        }
        // #endregion
    },
    unmounted() {
        document.removeEventListener('mousedown', this.onClickOutside);
        if (this.searchDebounceTimer)
            clearTimeout(this.searchDebounceTimer);
        this.searchAbortController?.abort();
    },
    // #endregion
    // #region Watch
    watch: {
        async dataAdapter() {
            await this.resetAdapter();
            await this.reloadData();
        },
        dataStore: {
            handler: async function () {
                await this.resetAdapter();
                await this.reloadData();
            },
            deep: true
        },
        async modelValue(newId) {
            if (!newId) {
                this.selectedItem = null;
                return;
            }
            if (this.selectedItem && String(this.selectedItem[this.idField]) === newId) {
                return;
            }
            await this.loadById(newId);
        },
        items() {
            this.highlightedIndex = -1;
        },
        searchQuery() {
            this.highlightedIndex = -1;
        },
    },
    // #endregion
    // #region Methods
    methods: {
        // #region Core
        async loadAllData() {
            let tAdapter = this.resolvedAdapter;
            {
                if (!tAdapter) {
                    return;
                }
            }
            this.isLoading = true;
            try {
                const searchFields = this.columns.map(c => c.field);
                this.items = await tAdapter.loadAll('', searchFields);
            }
            catch (e) {
                console.error('[GSearchField] loadAllData failed', e);
            }
            finally {
                this.isLoading = false;
            }
        },
        async loadData(page, append) {
            let tAdapter = this.resolvedAdapter;
            {
                if (!tAdapter) {
                    return;
                }
            }
            let signal;
            if (!append) {
                this.searchAbortController?.abort();
                const controller = new AbortController();
                this.searchAbortController = controller;
                signal = controller.signal;
                this.isLoading = true;
            }
            else {
                this.isLoadingMore = true;
            }
            try {
                const searchFields = this.columns.map(c => c.field);
                const { items, hasMore } = await tAdapter.loadPage(this.searchQuery, page, this.pageSize, searchFields, signal);
                this.hasMore = hasMore;
                this.items = append ? [...this.items, ...items] : items;
                this.currentPage = page;
            }
            catch (e) {
                if (e instanceof Error && e.name === 'AbortError')
                    return;
                console.error('[GSearchField] loadData failed', e);
            }
            finally {
                if (append) {
                    this.isLoadingMore = false;
                }
                else if (!signal?.aborted) {
                    this.isLoading = false;
                }
            }
        },
        async loadById(id) {
            let tAdapter = this.resolvedAdapter;
            {
                if (!tAdapter) {
                    return;
                }
            }
            this.isLoadingById = true;
            try {
                let tItem1 = this.items.find(e => e[this.idField] === id);
                {
                    if (tItem1) {
                        this.selectedItem = tItem1;
                        this.$emit('select', this.selectedItem);
                        return;
                    }
                }
                let tItem = await tAdapter.loadById(id);
                {
                    if (tItem) {
                        this.selectedItem = tItem;
                        this.$emit('select', this.selectedItem);
                    }
                    else if (id) {
                        let tNew = {};
                        {
                            tNew[this.idField] = id;
                            tNew[this.keyField] = id;
                            tNew[this.labelField] = '<NOT FOUND!>';
                        }
                        this.selectedItem = tNew;
                    }
                }
            }
            catch (e) {
                console.error('[GSearchField] loadById failed', e);
            }
            finally {
                this.isLoadingById = false;
            }
        },
        async resetAdapter() {
            this.adapter = null;
        },
        // #endregion
        // #region Navigation
        moveHighlight(direction) {
            const count = this.rowCount;
            if (count === 0)
                return;
            if (this.highlightedIndex < 0) {
                this.highlightedIndex = direction === 1 ? 0 : count - 1;
            }
            else {
                this.highlightedIndex = (this.highlightedIndex + direction + count) % count;
            }
            this.$nextTick(() => this.scrollHighlightedIntoView());
        },
        scrollHighlightedIntoView() {
            const dropdown = this.$refs.dropdownEl;
            const el = dropdown?.querySelector('[data-highlighted]');
            el?.scrollIntoView({ block: 'nearest' });
        },
        activateHighlighted() {
            if (this.dropdownMode === 'tree') {
                const node = this.visibleTreeNodes[this.highlightedIndex];
                if (!node)
                    return;
                if (this.selectLeafOnly && node.hasChildren) {
                    this.toggleTreeNode(String(node.item[this.idField]));
                }
                else {
                    this.selectItem(node.item);
                }
            }
            else {
                const item = this.displayItems[this.highlightedIndex];
                if (item)
                    this.selectItem(item);
            }
        },
        // #endregion
        // #region Helper
        findByKey(query) {
            const q = query.trim().toLowerCase();
            if (!q)
                return null;
            return this.items.find(item => {
                const v = item[this.keyField];
                return v != null && String(v).toLowerCase() === q;
            }) ?? null;
        },
        async reloadData() {
            //Data
            {
                this.items = [];
                this.selectedItem = null;
                this.currentPage = 1;
                this.hasMore = true;
            }
            if (this.mode === 'client') {
                await this.loadAllData();
            }
            if (this.modelValue) {
                await this.loadById(this.modelValue);
            }
        },
        buildChildrenMap() {
            const map = new Map();
            for (const item of this.items) {
                const parentId = item[this.parentField];
                if (parentId == null || parentId === '')
                    continue;
                const key = String(parentId);
                if (!map.has(key))
                    map.set(key, []);
                map.get(key).push(item);
            }
            return map;
        },
        toggleTreeNode(id) {
            const idx = this.expandedIds.indexOf(id);
            if (idx === -1) {
                this.expandedIds.push(id);
            }
            else {
                this.expandedIds.splice(idx, 1);
            }
            this.highlightedIndex = -1;
        },
        // #endregion
        // #region Events
        onMove(data) {
            // #region Vars
            let tDiff = window.innerHeight - (data.bottom ?? 0);
            let tHeight = (this.dropDownHeight === 0 ? 280 : this.dropDownHeight) + 10;
            // #endregion
            // #region Set
            this._inputPosition = {
                top: Math.round(data.top),
                left: Math.round(data.left),
                right: Math.round(data.right),
                bottom: Math.round(data.bottom),
                width: Math.round(data.right) - Math.round(data.left),
                height: Math.round(data.bottom) - Math.round(data.top),
                windowHeight: window.innerHeight,
                windowWidth: window.innerWidth,
                differenceToBottom: tDiff,
                dropDownHeight: tHeight,
                dropDownOnTop: tDiff < tHeight && tDiff > 0
            };
            // #endregion
        },
        handleInputGroupClick() {
            if (this.readonly)
                return;
            this.openDropdown();
        },
        handleFocus() {
            if (this.readonly)
                return;
            this.openDropdown();
        },
        handleSearchInput(event) {
            const value = event.target.value;
            this.searchQuery = value;
            if (this.mode === 'server') {
                if (this.searchDebounceTimer)
                    clearTimeout(this.searchDebounceTimer);
                this.searchDebounceTimer = setTimeout(() => {
                    this.currentPage = 1;
                    this.hasMore = true;
                    this.loadData(1, false);
                }, 500);
            }
        },
        async handleKeydown(event) {
            if (event.key === 'Escape') {
                this.closeDropdown();
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (!this.isOpen) {
                    await this.openDropdown();
                    return;
                }
                this.moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
                return;
            }
            if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && this.isOpen && this.dropdownMode === 'tree') {
                event.preventDefault();
                const node = this.visibleTreeNodes[this.highlightedIndex];
                if (!node?.hasChildren)
                    return;
                const id = String(node.item[this.idField]);
                const isExpanded = this.expandedIds.includes(id);
                const savedIndex = this.highlightedIndex;
                if (event.key === 'ArrowRight' && !isExpanded)
                    this.toggleTreeNode(id);
                if (event.key === 'ArrowLeft' && isExpanded)
                    this.toggleTreeNode(id);
                this.highlightedIndex = savedIndex;
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                // Hervorgehobenen Eintrag direkt übernehmen
                if (this.highlightedIndex >= 0) {
                    event.preventDefault();
                    this.activateHighlighted();
                    return;
                }
                // Schlüsselwert-Suche (ggf. Debounce vorher leeren)
                if (this.searchQuery.trim()) {
                    if (this.mode === 'server' && this.searchDebounceTimer) {
                        clearTimeout(this.searchDebounceTimer);
                        this.searchDebounceTimer = null;
                        await this.loadData(1, false);
                    }
                    const matched = this.findByKey(this.searchQuery);
                    if (matched) {
                        if (event.key === 'Enter')
                            event.preventDefault();
                        this.selectItem(matched);
                        return;
                    }
                }
                if (event.key === 'Tab')
                    this.closeDropdown();
            }
        },
        async handleScroll(event) {
            if (this.mode !== 'server' || !this.hasMore || this.isLoadingMore)
                return;
            const el = event.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                await this.loadData(this.currentPage + 1, true);
            }
        },
        toggleDropdown() {
            if (this.isOpen) {
                this.closeDropdown();
            }
            else {
                this.openDropdown();
            }
        },
        async openDropdown() {
            if (this.isOpen || this.readonly) {
                return;
            }
            this.onMove(this.$refs.input_group.getBoundingClientRect());
            this.searchQuery = '';
            this.highlightedIndex = -1;
            this.isOpen = true;
            if (this.mode === 'server') {
                this.currentPage = 1;
                this.hasMore = true;
                await this.loadData(1, false);
            }
            this.$nextTick(() => {
                const input = this.$refs.inputEl;
                input?.focus();
            });
        },
        closeDropdown() {
            this.isOpen = false;
            this.searchQuery = '';
        },
        selectItem(item) {
            this.selectedItem = item;
            const id = String(item[this.idField]);
            this.$emit('update:modelValue', id);
            this.$emit('select', item);
            this.closeDropdown();
        },
        clearSelection() {
            this.selectedItem = null;
            this.$emit('update:modelValue', null);
            this.$emit('select', null);
        },
        onClickOutside(event) {
            const root = this.$refs.root;
            if (root && !root.contains(event.target)) {
                this.closeDropdown();
            }
        },
        // #endregion
    },
    // #endregion
});
// #endregion
